import { createContext, useContext } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { clsx } from 'clsx';

export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  children: ReactNode;
  /**
   * Calm-v2 styling for the whole table: flat surface container (1px line
   * border, radius 10, no shadow), surfaceSoft header with 11px uppercase
   * muted labels, 1px line row dividers, no zebra (the `index` prop on rows
   * is ignored), hover = surfaceSoft. Propagates to all sub-components via
   * context. Only valid in apps that import `@elka/theme` v2/calm.css.
   * Default (false) keeps legacy styling.
   */
  calm?: boolean;
}

export interface TableHeaderProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode;
}

export interface TableBodyProps extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode;
}

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  children: ReactNode;
  isClickable?: boolean;
  /** Row index for alternating background colors (even=white, odd=#F4F6FA). Ignored in calm tables (no zebra). */
  index?: number;
}

export interface TableHeadProps extends HTMLAttributes<HTMLTableCellElement> {
  children: ReactNode;
}

export interface TableCellProps extends HTMLAttributes<HTMLTableCellElement> {
  children: ReactNode;
}

const TableCalmContext = createContext(false);

export function Table({ children, className, calm = false, ...props }: TableProps) {
  return (
    <TableCalmContext.Provider value={calm}>
      <div
        className={
          calm
            ? 'rounded-card bg-surface border-line overflow-x-auto border'
            : 'table-container overflow-x-auto'
        }
      >
        <table className={clsx('w-full text-sm', className)} {...props}>
          {children}
        </table>
      </div>
    </TableCalmContext.Provider>
  );
}

export function TableHeader({ children, className, ...props }: TableHeaderProps) {
  const calm = useContext(TableCalmContext);
  return (
    <thead
      className={clsx(
        calm
          ? 'border-line bg-surface-soft border-b'
          : 'border-b border-[rgba(60,95,168,0.1)] bg-gradient-to-b from-[#F8FAFC] to-[#F4F6FA]/80',
        className
      )}
      {...props}
    >
      {children}
    </thead>
  );
}

export function TableBody({ children, className, ...props }: TableBodyProps) {
  const calm = useContext(TableCalmContext);
  return (
    <tbody
      className={clsx(calm ? 'divide-line divide-y' : 'divide-y divide-gray-100', className)}
      {...props}
    >
      {children}
    </tbody>
  );
}

export function TableRow({ children, className, isClickable, index, ...props }: TableRowProps) {
  const calm = useContext(TableCalmContext);

  if (calm) {
    return (
      <tr
        className={clsx(
          'bg-surface hover:bg-surface-soft transition-colors duration-150',
          isClickable && 'cursor-pointer',
          className
        )}
        {...props}
      >
        {children}
      </tr>
    );
  }

  const isEvenRow = index !== undefined ? index % 2 === 0 : true;
  const rowBgClass = isEvenRow ? 'bg-white' : 'bg-[#F4F6FA]';

  return (
    <tr
      className={clsx(
        rowBgClass,
        'transition-colors duration-150 hover:bg-[#F0F3FA]',
        isClickable && 'cursor-pointer',
        className
      )}
      {...props}
    >
      {children}
    </tr>
  );
}

export function TableHead({ children, className, ...props }: TableHeadProps) {
  const calm = useContext(TableCalmContext);
  return (
    <th
      className={clsx(
        calm
          ? 'text-muted px-4 py-2.5 text-right text-[11px] font-bold tracking-wide uppercase'
          : 'px-4 py-3 text-right text-xs font-semibold tracking-wide text-gray-500 uppercase',
        className
      )}
      {...props}
    >
      {children}
    </th>
  );
}

export function TableCell({ children, className, ...props }: TableCellProps) {
  const calm = useContext(TableCalmContext);
  return (
    <td
      className={clsx(
        calm
          ? 'text-ink-2 px-4 py-3 text-[13px]'
          : 'border-l border-[rgba(60,95,168,0.06)] px-4 py-3 text-gray-600',
        className
      )}
      {...props}
    >
      {children}
    </td>
  );
}
